import { useEffect } from 'react';
import { EntityListPage } from '../components/templates/EntityListPage';
import { Badge, CardActions, CompactRow, CompactRowList, Callout, LoadMorePager } from '../components/ui';
import { listInspections, inspectionsKeys, type InspectionListItem } from '../lib/api/inspections.gen';
import { useCursorList } from '../lib/query/useCursorList';
import { formatDistanceToNow } from '../lib/time';
import { useAuth } from '../lib/auth';
import { useVocab } from '../lib/vocab';
import { useUrlState, urlStateInt, type UrlStateSchema } from '../hooks/useUrlState';
import { errorMessage } from '../lib/errorMessage';

// Activity feed (story 2-146a — collection-page adoption; story 2-72c moved
// the underlying read onto the real cursor contract). Moved onto the same
// EntityListPage + useUrlState shape every other list in this lane composes
// (SitesPage / TemplateLibraryPage / ItemsListPage / ReviewQueuePage /
// ReportsListPage), preserving this page's own actor → action → subject →
// time hierarchy rather than redesigning it.
//
// WHY THIS NOW FOLLOWS `TemplateLibraryPage`'s SHAPE, NOT ITS OWN
// PRE-2-72c ONE. `GET /v1/inspections` (`listInspections()` below) answers
// the shared cursor envelope (G-18) as of story 2-72c — the pre-migration
// comment here recorded that it did not, and that pagination was therefore
// a client-side slice over an already-`.limit(200)`-capped array (the SAME
// constraint `ItemsListPage`'s own comment recorded, filed as a finding by
// that story, fixed by this one). This page moves onto `useCursorList` +
// its URL-owned page-count restore effect for the same reason ItemsListPage
// does — see that page's own comment for the mechanism.
//
// WHAT "ACTOR" MEANS HERE, HONESTLY. The feed already renders action (AI
// analysis completed/pending) → subject (the site name, or the inspection's
// own id when the site relation cannot be read) → time (relative timestamp)
// — unchanged from before this migration. It does NOT render a resolved
// actor name: `Inspection.createdBy` is a bare user id (2-72c's
// `InspectionListItem` DTO does not project it, same as every other field
// this page never read), and resolving it to a name is a backend read this
// story is not scoped to add (P08's audit trail — `AuditEvent.actorId` — is
// a separate, currently unlisted, model with no list endpoint at all). This
// is a pre-existing gap in the DATA, not something the collection-contract
// move can paper over from the frontend; the status badge stays the closest
// available "who/what changed" signal.
//
// Vocabulary layer (P03 §5, story 3-2): every inspection is building-domain
// today, so the unqualified default-domain noun is exact.

const PAGE_SIZE = 20;

const activityUrlSchema: UrlStateSchema<{ page: number }> = {
  page: urlStateInt(0),
};

export function ActivityPage(): JSX.Element {
  const { user } = useAuth();
  const { vocab } = useVocab();
  const unitsLower = vocab('Units').toLowerCase();
  const [urlState, setUrlState] = useUrlState(activityUrlSchema);
  const { page } = urlState;

  // Most-recently-updated first — the same "what changed" reading order the
  // feed has always used (the endpoint's own `updatedAt desc` order, never
  // re-sorted client-side).
  const {
    items: sorted,
    total,
    pageCount,
    hasMore,
    fetchNextPage,
    isLoading,
    isLoadingMore,
    isError,
    error,
    refetch,
  } = useCursorList<InspectionListItem>({
    queryKey: inspectionsKeys.list({ org: user?.organizationId, feed: 'activity' }),
    fetchPage: (pageParams) => listInspections(pageParams),
    limit: PAGE_SIZE,
    enabled: Boolean(user),
  });

  // Story 2-72c (G-25): restore the URL-owned page count on a deep link or
  // reload — the same mechanism `ItemsListPage`'s identical effect uses.
  useEffect(() => {
    if (isLoading || isLoadingMore) return;
    if (pageCount === 0) return; // first page still in flight
    if (page + 1 > pageCount && hasMore) {
      fetchNextPage();
    }
  }, [isLoading, isLoadingMore, pageCount, page, hasMore, fetchNextPage]);

  const visible = sorted;

  if (isError) {
    return (
      <Callout
        tone="danger"
        title="The activity feed could not be loaded"
        headingLevel={2}
        actions={
          <CardActions
            align="start"
            actions={[{ id: 'retry', label: 'Try again', variant: 'secondary', onClick: () => refetch() }]}
          />
        }
      >
        {errorMessage(error)}
      </Callout>
    );
  }

  return (
    <EntityListPage
      title="Activity feed"
      description="Track uploads, AI summaries, and exports across your organization."
      isLoading={isLoading}
      empty={{
        title: 'No activity recorded yet',
        description: 'Run an inspection to populate this feed.',
      }}
      footer={
        // Story 2-147tb: converged onto the canonical `LoadMorePager`
        // (P10 §2.7 row 7). Story 2-72c: `onLoadMore` bumps the URL-owned
        // page count rather than calling `fetchNextPage()` directly — the
        // restore effect above is what actually fetches, same split
        // `ItemsListPage`'s identical footer uses.
        <LoadMorePager
          shownCount={visible.length}
          total={total}
          hasMore={hasMore}
          onLoadMore={() => setUrlState({ page: page + 1 })}
          isLoadingMore={isLoadingMore}
        />
      }
    >
      {visible.length > 0 && (
        <CompactRowList aria-label="Activity feed" data-testid="activity-feed">
          {visible.map((inspection) => (
            <CompactRow
              key={inspection._id}
              data-testid={`activity-row-${inspection._id}`}
              title={`AI analysis ${inspection.aiSummary ? 'completed' : 'pending'} for ${
                inspection.propertyId?.name ?? `#${inspection._id.slice(-4)}`
              }`}
              subtitle={`${inspection.roomsCount} ${unitsLower}`}
              meta={
                <>
                  <span className="text-xs text-muted-foreground">{formatDistanceToNow(inspection.updatedAt)}</span>
                  <Badge tone="neutral">{inspection.status}</Badge>
                </>
              }
            />
          ))}
        </CompactRowList>
      )}
    </EntityListPage>
  );
}
